/**
 * @file Vie des lieux du hub : fumee animee des cheminees (une source par maison) et poissons sauteurs des lacs / etangs (arc de cercle,
 *       eclaboussure, ronds dans l'eau). Tout est analytique (fonction du temps), sans allocation par frame, borne par des pools fixes et
 *       limite aux lieux effectivement presents (instancies) cette frame.
 */
import {
  Color,
  DoubleSide,
  DynamicDrawUsage,
  InstancedBufferAttribute,
  InstancedMesh,
  Matrix4,
  Mesh,
  MeshBasicMaterial,
  Quaternion,
  RingGeometry,
  Vector3,
} from 'three';
import type { BufferGeometry, Scene } from 'three';
import { houseChimney } from '../factories/ThemeKit';
import { MergeBuilder } from '../factories/MergeBuilder';
import type { GeometryLibrary } from '../gpu/GeometryLibrary';
import type { TargetPalette } from '@config/types';
import type { ToonKit } from '../toon/toon';
import { HouseCollapse } from './HouseCollapse';

const TAU = Math.PI * 2;
/** Bornes des pools. */
const MAX_HOUSES = 12;
const PUFFS_PER_HOUSE = 9;
const MAX_LAKES = 6;
const DROPS_PER_EVENT = 5;
const DROPS_PER_LAKE = DROPS_PER_EVENT * 2;

/** Fumee : couleur de depart (claire) ; en hiver plus grise (se detache de la neige) et plus dense. */
const SMOKE_COLOR = new Color('#f3eff7');
const SMOKE_COLOR_WINTER = new Color('#cfc9db');
const SMOKE_RISE = 2.9;

/** Poisson : saut (s), hauteur au sommet (u), pause mini entre deux sauts. */
const JUMP_SECONDS = 0.95;
const JUMP_HEIGHT = 1.25;
const RIPPLE_SECONDS = 1.1;
const SPLASH_SECONDS = 0.6;
const GRAVITY = 9;
/** Rayon (x echelle du lac) dans lequel le poisson saute : le centre profond du plan d'eau (rayon de l'eau ~4.6). */
const JUMP_AREA = 2.3;

const hash01 = (n: number): number => {
  const x = Math.sin(n * 12.9898 + 78.233) * 43758.5453;
  return x - Math.floor(x);
};
const frac = (v: number): number => v - Math.floor(v);

interface LakeSlot {
  id: number;
  x: number;
  z: number;
  scale: number;
  active: boolean;
  fish: Mesh;
  ringOut: Mesh;
  ringIn: Mesh;
}

export class PlacesFx {
  // --- fumee
  private readonly smoke: InstancedMesh;
  private readonly smokeColors: InstancedBufferAttribute;
  private readonly hx = new Float32Array(MAX_HOUSES);
  private readonly hy = new Float32Array(MAX_HOUSES);
  private readonly hz = new Float32Array(MAX_HOUSES);
  private houses = 0;
  // --- poissons
  private readonly lakes: LakeSlot[] = [];
  private readonly drops: InstancedMesh;
  private readonly m = new Matrix4();
  private readonly q = new Quaternion();
  private readonly qTmp = new Quaternion();
  private readonly p = new Vector3();
  private readonly s = new Vector3();
  private readonly c = new Color();
  private readonly zero = new Matrix4().makeScale(0, 0, 0);
  private readonly bg = new Color('#cdeefe');
  private lakeCount = 0;
  /** Ecroulements de maisons (une maison detruite s'effondre en morceaux). */
  private readonly collapse: HouseCollapse;
  private readonly collapsing = new Map<number, number>();
  private frame = 0;
  private lastTime = -1;

  constructor(
    private readonly scene: Scene,
    private readonly kit: ToonKit,
    private readonly geo: GeometryLibrary,
  ) {
    this.collapse = new HouseCollapse(scene, kit, geo);
    // fumee : sphere toon (ombre douce), couleur d'instance = teinte qui s'eclaircit vers le fond
    const puff = geo.ico(1, 1);
    this.smoke = new InstancedMesh(puff, kit.toon('#ffffff'), MAX_HOUSES * PUFFS_PER_HOUSE);
    this.smoke.instanceMatrix.setUsage(DynamicDrawUsage);
    this.smokeColors = new InstancedBufferAttribute(new Float32Array(MAX_HOUSES * PUFFS_PER_HOUSE * 3).fill(1), 3);
    this.smokeColors.setUsage(DynamicDrawUsage);
    this.smoke.instanceColor = this.smokeColors;
    this.smoke.frustumCulled = false;
    this.smoke.count = 0;
    this.smoke.visible = false;
    scene.add(this.smoke);

    // gouttes d'eclaboussure
    const dropMat = kit.track(new MeshBasicMaterial({ color: 0xe8f8ff }));
    this.drops = new InstancedMesh(geo.ico(1, 0), dropMat, MAX_LAKES * DROPS_PER_LAKE);
    this.drops.instanceMatrix.setUsage(DynamicDrawUsage);
    this.drops.frustumCulled = false;
    this.drops.count = 0;
    this.drops.visible = false;
    scene.add(this.drops);

    // poissons et ronds dans l'eau (un jeu par plan d'eau, cree une fois)
    const fishGeo = this.geo.get('hub:fish', () => this.buildFish());
    const fishMat = kit.toon('#ffffff', { vertexColors: true });
    const ringGeo = this.geo.get('hub:ripple', () => {
      const g = new RingGeometry(0.9, 1, 40, 1);
      g.rotateX(-Math.PI / 2);
      return g;
    });
    for (let i = 0; i < MAX_LAKES; i++) {
      const fish = new Mesh(fishGeo, fishMat);
      fish.add(kit.outlineFor(fishGeo));
      fish.visible = false;
      fish.frustumCulled = false;
      const ring = (): Mesh => {
        const mat = kit.track(new MeshBasicMaterial({ color: 0xffffff, transparent: true, opacity: 0, depthWrite: false, side: DoubleSide, polygonOffset: true, polygonOffsetFactor: -2, polygonOffsetUnits: -2 }));
        const mesh = new Mesh(ringGeo, mat);
        mesh.visible = false;
        mesh.frustumCulled = false;
        mesh.renderOrder = 1;
        return mesh;
      };
      const slot: LakeSlot = { id: -1, x: 0, z: 0, scale: 1, active: false, fish, ringOut: ring(), ringIn: ring() };
      scene.add(fish, slot.ringOut, slot.ringIn);
      this.lakes.push(slot);
    }
  }

  /** Debut de frame : les lieux sont (re)declares par `house` / `lake` pendant la synchronisation des entites. */
  begin(): void {
    this.frame++;
    this.houses = 0;
    this.lakeCount = 0;
    for (const l of this.lakes) l.active = false;
  }

  /** Une maison : position sim (x, y), lacet de la cible, variante, echelle monde (def.visual.scale). */
  house(x: number, y: number, rot: number, variant: number, scale: number): void {
    if (this.houses >= MAX_HOUSES) return;
    const c = houseChimney(variant);
    const lx = c.x * scale;
    const lz = c.z * scale;
    const cos = Math.cos(rot);
    const sin = Math.sin(rot);
    const i = this.houses++;
    this.hx[i] = x + lx * cos + lz * sin;
    this.hz[i] = -y - lx * sin + lz * cos;
    this.hy[i] = (c.y + c.h / 2 + 0.04) * scale;
  }

  /**
   * Une maison en train de mourir : l'instance (non dessinee) est remplacee par un ecroulement en morceaux, lance au premier appel
   * de la mort (ou apres une interruption : la meme entite qui revient a la vie puis remeurt rejoue l'ecroulement).
   */
  collapseHouse(id: number, x: number, y: number, rot: number, variant: number, scale: number, palette: TargetPalette): void {
    const last = this.collapsing.get(id);
    this.collapsing.set(id, this.frame);
    if (last !== undefined && last >= this.frame - 1) return;
    this.collapse.start({ id, x, y, rot, variant, scale, palette });
    if (this.collapsing.size > 32) for (const [k, f] of this.collapsing) if (f < this.frame - 2) this.collapsing.delete(k);
  }

  /** Un plan d'eau (lac / etang) : id d'entite, position sim, echelle monde. */
  lake(id: number, x: number, y: number, scale: number): void {
    const slot = this.lakes[this.lakeCount];
    if (!slot) return;
    this.lakeCount++;
    slot.id = id;
    slot.x = x;
    slot.z = -y;
    slot.scale = scale;
    slot.active = true;
  }

  /** Fin de frame : anime fumees et poissons. `winter` : fumee plus dense et plus grise. */
  update(time: number, winter: boolean): void {
    this.updateSmoke(time, winter);
    this.updateFish(time);
    const dt = this.lastTime < 0 ? 0 : Math.min(0.1, Math.max(0, time - this.lastTime));
    this.lastTime = time;
    this.collapse.update(dt);
  }

  dispose(): void {
    this.collapse.dispose();
    this.scene.remove(this.smoke, this.drops);
    this.smoke.dispose();
    this.drops.dispose();
    for (const l of this.lakes) this.scene.remove(l.fish, l.ringOut, l.ringIn);
    this.lakes.length = 0;
  }

  // ------------------------------------------------------------------------------------------------------------------

  private updateSmoke(time: number, winter: boolean): void {
    const n = winter ? PUFFS_PER_HOUSE : 6;
    const period = winter ? 4.6 : 3.8;
    const sizeK = winter ? 1.35 : 1;
    const base = winter ? SMOKE_COLOR_WINTER : SMOKE_COLOR;
    const fogColor = this.fogColor();
    let k = 0;
    for (let h = 0; h < this.houses; h++) {
      const seed = hash01(h * 7.31 + this.hx[h]! * 0.13);
      for (let j = 0; j < n; j++) {
        const age = frac(time / period + j / n + seed);
        // derive douce vers +x / +z, qui s'amplifie avec l'altitude
        const drift = Math.pow(age, 1.4) * 1.9;
        const sway = Math.sin(age * 6 + seed * TAU + j) * 0.14 * age;
        this.p.set(this.hx[h]! + drift + sway, this.hy[h]! + age * SMOKE_RISE, this.hz[h]! + drift * 0.35 + sway * 0.6);
        const grow = Math.min(1, age * 7);
        const size = (0.2 + 0.62 * Math.pow(age, 0.8)) * sizeK * grow * (age > 0.9 ? (1 - age) * 10 : 1);
        this.s.set(size, size * 0.9, size);
        this.q.identity();
        this.m.compose(this.p, this.q, this.s);
        this.smoke.setMatrixAt(k, this.m);
        // s'eclaircit : la teinte glisse vers la couleur du fond avec l'altitude
        const fadeT = Math.min(1, Math.max(0, (age - 0.25) / 0.75));
        this.c.copy(base).lerp(fogColor, fadeT * (winter ? 0.7 : 0.85));
        this.c.toArray(this.smokeColors.array, k * 3);
        k++;
      }
    }
    this.smoke.count = k;
    this.smoke.visible = k > 0;
    if (k > 0) {
      this.smoke.instanceMatrix.needsUpdate = true;
      this.smokeColors.needsUpdate = true;
    }
  }

  /** Couleur du fond de scene (brouillard) : la fumee s'y dissout. */
  private fogColor(): Color {
    const fog = this.scene.fog;
    const bg = this.scene.background;
    if (fog) this.bg.copy(fog.color);
    else if (bg instanceof Color) this.bg.copy(bg);
    return this.bg;
  }

  private updateFish(time: number): void {
    let d = 0;
    for (const l of this.lakes) {
      if (!l.active) {
        l.fish.visible = false;
        l.ringOut.visible = false;
        l.ringIn.visible = false;
        continue;
      }
      const idx = l.id;
      const cycle = 6.5 + 5 * hash01(idx * 1.7);
      const t = time + cycle * hash01(idx * 3.1);
      const n = Math.floor(t / cycle);
      const tIn = t - n * cycle;
      const R = JUMP_AREA * l.scale;
      // points de sortie A et de rentree B tires par saut (depuis la graine du saut)
      const a0 = hash01(idx * 5.3 + n * 11.7) * TAU;
      const ra = R * (0.3 + 0.55 * hash01(idx + n * 2.3));
      const ax = l.x + Math.cos(a0) * ra;
      const az = l.z + Math.sin(a0) * ra;
      const a1 = a0 + Math.PI + (hash01(idx * 9.1 + n * 4.1) - 0.5) * 1.4;
      const rb = R * (0.3 + 0.55 * hash01(idx * 1.3 + n * 8.9));
      const bx = l.x + Math.cos(a1) * rb;
      const bz = l.z + Math.sin(a1) * rb;
      const wy = 0.08 * l.scale;
      const fs = 0.8 + 0.25 * l.scale;

      // poisson en l'air
      if (tIn < JUMP_SECONDS) {
        const k = tIn / JUMP_SECONDS;
        const height = JUMP_HEIGHT * (0.7 + 0.3 * l.scale);
        const hx = bx - ax;
        const hz = bz - az;
        const px = ax + hx * k;
        const pz = az + hz * k;
        const py = wy + 4 * height * k * (1 - k) + 0.1;
        const vh = Math.hypot(hx, hz) / JUMP_SECONDS;
        const vy = (4 * height * (1 - 2 * k)) / JUMP_SECONDS;
        l.fish.visible = true;
        l.fish.position.set(px, py, pz);
        l.fish.rotation.set(-Math.atan2(vy, Math.max(0.01, vh)), Math.atan2(hx, hz), Math.sin(time * 38) * 0.25, 'YXZ');
        l.fish.scale.setScalar(fs);
      } else {
        l.fish.visible = false;
      }

      // ronds dans l'eau a la sortie (A) et a la rentree (B)
      this.ripple(l.ringOut, ax, az, wy, tIn, R);
      this.ripple(l.ringIn, bx, bz, wy, tIn - JUMP_SECONDS, R);

      // eclaboussures
      d = this.splash(d, ax, az, wy, tIn, idx * 13.1 + n, l.scale);
      d = this.splash(d, bx, bz, wy, tIn - JUMP_SECONDS, idx * 17.9 + n + 0.5, l.scale);
    }
    for (let i = d; i < this.drops.count; i++) this.drops.setMatrixAt(i, this.zero);
    this.drops.count = d;
    this.drops.visible = d > 0;
    if (d > 0) this.drops.instanceMatrix.needsUpdate = true;
  }

  private ripple(mesh: Mesh, x: number, z: number, y: number, age: number, area: number): void {
    const on = age >= 0 && age < RIPPLE_SECONDS;
    mesh.visible = on;
    if (!on) return;
    const k = age / RIPPLE_SECONDS;
    const r = (0.15 + 0.85 * (1 - (1 - k) * (1 - k))) * Math.max(0.5, area * 0.5);
    mesh.position.set(x, y + 0.03, z);
    mesh.scale.set(r, 1, r);
    (mesh.material as MeshBasicMaterial).opacity = (1 - k) * 0.85;
  }

  /** Gouttes d'un point d'impact : jets balistiques, retirees au contact de l'eau. Retourne le nouvel indice d'instance. */
  private splash(index: number, x: number, z: number, y: number, age: number, seed: number, scale: number): number {
    if (age < 0 || age >= SPLASH_SECONDS) return index;
    let i = index;
    for (let k = 0; k < DROPS_PER_EVENT; k++) {
      const a = hash01(seed + k * 3.7) * TAU;
      const sp = (0.5 + 0.9 * hash01(seed + k * 7.1)) * (0.6 + 0.4 * scale);
      const vy = 2.6 + 2.2 * hash01(seed + k * 5.3);
      const py = y + vy * age - 0.5 * GRAVITY * age * age;
      if (py < y) continue;
      const size = (0.06 + 0.04 * hash01(seed + k)) * (1 - age / SPLASH_SECONDS * 0.4);
      this.p.set(x + Math.cos(a) * sp * age, py, z + Math.sin(a) * sp * age);
      this.q.identity();
      this.s.set(size, size, size);
      this.m.compose(this.p, this.q, this.s);
      this.drops.setMatrixAt(i++, this.m);
    }
    return i;
  }

  /** Poisson kawaii : corps orange, tache blanche sur le dos, queue et nageoire dorsale, gros yeux. Axe +Z = avant. */
  private buildFish(): BufferGeometry {
    const g = this.geo;
    const b = new MergeBuilder();
    const ORANGE = '#ff9a3c';
    const DARK_ORANGE = '#f06a2c';
    b.add(g.sphere(1, 12, 9), ORANGE, { sx: 0.17, sy: 0.18, sz: 0.36 });
    b.add(g.sphere(1, 10, 8), '#ffffff', { y: 0.1, z: -0.06, sx: 0.12, sy: 0.08, sz: 0.2 });
    b.add(g.sphere(1, 8, 6), DARK_ORANGE, { z: -0.4, sx: 0.03, sy: 0.2, sz: 0.17 });
    b.add(g.sphere(1, 8, 6), DARK_ORANGE, { y: 0.2, z: 0.0, sx: 0.025, sy: 0.1, sz: 0.15 });
    for (const sx of [-1, 1]) {
      b.add(g.sphere(0.05, 8, 6), '#ffffff', { x: sx * 0.11, y: 0.05, z: 0.22, outline: false });
      b.add(g.sphere(0.028, 6, 5), '#2b2017', { x: sx * 0.125, y: 0.05, z: 0.255, outline: false });
    }
    const fill = b.fill('main');
    if (!fill) throw new Error('PlacesFx: poisson vide');
    return fill;
  }
}
